import { FormEvent, useState } from 'react';
import Layout from '@/components/feature/Layout';
import { clinic } from '@/mocks/site';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Book() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honey = String(fd.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }
    fd.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    fd.forEach((v, k) => body.append(k, String(v)));

    try {
      const res = await fetch('https://readdy.ai/api/form/dakgvqkov550s8tco7g0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const msg: string = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '';
      const ok = res.ok && (parsed?.code === 'OK') && !/spam/i.test(msg);
      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(msg || 'Something went wrong. Please try again or call us.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again or call us.');
    }
  };

  return (
    <Layout>
      {/* COMPACT SUBPAGE INTRO */}
      <section className="relative overflow-hidden bg-white">
        <span className="deco-float-sm absolute pointer-events-none hidden md:block" style={{ width: 360, height: 360, top: -160, right: -120, background: '#F2D16B', opacity: 0.45, borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
        <div className="relative max-w-container mx-auto px-5 md:px-12 pt-16 md:pt-20 pb-10 md:pb-14">
          <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-cocoa">Book a Visit</div>
          <h1 className="font-display mt-4 font-bold leading-[1.0] text-cocoa text-[38px] md:text-[52px] max-w-[820px]">
            Let's find a good time for you and your pet.
          </h1>
          <p className="mt-5 text-[16px] md:text-[17px] leading-relaxed text-ink/75 max-w-[620px]">
            Tell us a little about your pet and when works for you — we'll confirm your appointment within one business day.
          </p>
        </div>
      </section>

      <section className="relative overflow-hidden pb-24 md:pb-32 pt-6 md:pt-10" style={{ background: '#FFF9F2' }}>
        <span className="deco-float-lg absolute pointer-events-none hidden md:block" style={{ width: 520, height: 520, bottom: -220, left: -180, background: '#FBEBD6', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
        <div className="relative max-w-container mx-auto px-5 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            {status === 'success' ? (
              <div className="relative overflow-hidden rounded-[32px] border border-cocoa/15 p-10 md:p-14" style={{ background: '#F2D16B' }}>
                <span className="deco-float-sm-alt absolute pointer-events-none" style={{ width: 320, height: 320, top: -140, right: -100, background: '#F8E4A8', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
                <div className="relative">
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-cocoa">
                    <i className="ri-check-line text-[26px]"></i>
                  </div>
                  <h2 className="mt-6 font-display font-bold text-[32px] md:text-[42px] leading-[1.05] text-cocoa max-w-[560px]">
                    Thanks — your request is in.
                  </h2>
                  <p className="mt-4 text-[16px] md:text-[17px] text-ink/80 leading-relaxed max-w-[520px]">
                    Our team will contact you to confirm your appointment. If it's urgent, please call us at {clinic.phone}.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="btn-motion group mt-8 inline-flex items-center gap-2 h-[52px] px-7 rounded-full bg-cocoa hover:bg-careDark text-white text-[15px] font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Request another visit
                    <i className="link-arrow-icon ri-arrow-right-line text-[16px]"></i>
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-readdy-form
                id="appointment-request-form"
                className="rounded-[32px] border border-cocoa/15 bg-white p-6 md:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Owner Name" name="owner_name" required />
                  <Field label="Email" name="email" type="email" required />
                  <Field label="Phone" name="phone" type="tel" required />
                  <Field label="Pet Name" name="pet_name" required />
                  <SelectField label="Pet Type" name="pet_type" options={['Dog','Cat','Rabbit','Small companion','Other']} required />
                  <SelectField label="Reason for Visit" name="reason" options={['Wellness exam','Vaccination','Dental care','Sick / urgent visit','Surgery consult','Other']} required />
                  <Field label="Preferred Date" name="preferred_date" type="date" required />
                  <SelectField label="Preferred Time" name="preferred_time" options={['Morning (8:00 – 11:30)','Midday (11:30 – 14:00)','Afternoon (14:00 – 17:00)','Late (17:00 – 18:30)']} required />
                </div>

                <div className="mt-5">
                  <label className="block text-[14px] font-medium text-ink mb-2">Message</label>
                  <textarea
                    name="message"
                    maxLength={500}
                    rows={5}
                    className="w-full rounded-[20px] border border-cocoa/15 bg-[#FFF9F2] px-4 py-3 text-[15px] text-ink placeholder:text-ink/40 focus:outline-none focus:border-cocoa/50 focus:bg-white transition-colors"
                    placeholder="Anything we should know before the visit?"
                  />
                </div>

                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-honeypot" />

                {status === 'error' && (
                  <div className="mt-6 rounded-[16px] border border-cocoa/20 bg-[#FFF9F2] text-ink px-4 py-3 text-[14px]">{formError}</div>
                )}

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="btn-motion group inline-flex items-center justify-center gap-2 h-[54px] px-8 rounded-full bg-cocoa hover:bg-careDark text-white text-[15px] font-semibold cursor-pointer whitespace-nowrap disabled:opacity-70"
                  >
                    {status === 'submitting' ? 'Sending…' : 'Request Appointment'}
                    <i className="link-arrow-icon ri-arrow-right-line text-[17px]"></i>
                  </button>
                  <p className="text-[13px] text-ink/60">We'll confirm within one business day.</p>
                </div>
              </form>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="relative overflow-hidden rounded-[32px] p-8" style={{ background: '#F2D16B' }}>
              <span className="deco-float-sm absolute pointer-events-none" style={{ width: 260, height: 260, bottom: -120, right: -80, background: '#F8E4A8', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
              <div className="relative">
                <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-cocoa/80">Prefer to call?</div>
                <a
                  href={`tel:${clinic.phone.replace(/[^0-9+]/g,'')}`}
                  className="mt-3 block font-display font-bold text-[28px] text-cocoa cursor-pointer hover:opacity-80"
                >
                  {clinic.phone}
                </a>
                <div className="mt-7 space-y-2 text-[14px] text-ink/80">
                  {clinic.hours.map(h => (
                    <div key={h.day} className="flex justify-between gap-4">
                      <span>{h.day}</span>
                      <span>{h.time}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-7 pt-5 border-t border-cocoa/20 text-[14px] text-ink/80 leading-relaxed">
                  {clinic.address}
                </div>
              </div>
            </div>

            <div className="rounded-[32px] border border-cocoa/15 bg-white p-7">
              <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-cocoa">Emergency?</div>
              <p className="mt-3 text-[14px] text-ink/75 leading-relaxed">
                If your pet needs urgent after-hours care, contact Rose City Veterinary Emergency at (503) 555-0199.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, name, type = 'text', required }: { label: string; name: string; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="block text-[14px] font-medium text-ink mb-2">
        {label}{required && <span className="text-cocoa"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        className="w-full h-[54px] rounded-full border border-cocoa/15 bg-[#FFF9F2] px-5 text-[15px] text-ink focus:outline-none focus:border-cocoa/50 focus:bg-white transition-colors"
      />
    </div>
  );
}

function SelectField({ label, name, options, required }: { label: string; name: string; options: string[]; required?: boolean }) {
  return (
    <div>
      <label className="block text-[14px] font-medium text-ink mb-2">
        {label}{required && <span className="text-cocoa"> *</span>}
      </label>
      <select
        name={name}
        required={required}
        defaultValue=""
        className="w-full h-[54px] rounded-full border border-cocoa/15 bg-[#FFF9F2] px-5 text-[15px] text-ink focus:outline-none focus:border-cocoa/50 focus:bg-white transition-colors cursor-pointer"
      >
        <option value="" disabled>Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}